import React from "react"
import { LoadingOverlay, Modal, ScrollArea, Stack } from "@mantine/core"
import { Button } from "@quorumus/plum"
import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/AI/SamplePreviewModal.module.css"
import { useQuery } from "@tanstack/react-query"
import { useFormContext } from "react-hook-form"
import { generatePlaceholdersForPreview } from "@/services/campaigns"

export interface Preview {
    text: string
    placeholders: Record<string, string>
    answers: Record<string, string>
}
interface Props {
    text: string
    opened: boolean
    onClose: () => void
    placeholders: {
        group: string
        items: {
            label: string
            value: string
        }[]
    }[]
}

const fetchPreviewPlaceholders = async ({ queryKey }) => {
    const { text, tone, length } = queryKey[1]
    return await generatePlaceholdersForPreview({ message: text, tone, length: length })
}

const segregatePlaceholders = (allAnswersPlaceholders: string[], fetchedPlaceholders: Record<string, string>) => {
    const answersPlaceholders = {}
    const commonPlaceholders = {}
    for (const placeholder of allAnswersPlaceholders) {
        if (fetchedPlaceholders[placeholder]) {
            answersPlaceholders[placeholder] = fetchedPlaceholders[placeholder]
        }
    }
    for (const placeholder in fetchedPlaceholders) {
        if (!answersPlaceholders[placeholder]) {
            commonPlaceholders[placeholder] = fetchedPlaceholders[placeholder]
        }
    }
    return { answersPlaceholders, commonPlaceholders }
}

export const SamplePreviewModal = (props: Props) => {
    const formContext = useFormContext()
    const tone = formContext.getValues("ai_options.tone")
    const length = formContext.getValues("ai_options.length")
    const previewQuery = useQuery<Record<string, string>>({
        queryKey: ["ai-personalized-message-preview", { text: props.text, tone, length }],
        queryFn: fetchPreviewPlaceholders,
        enabled: props.opened,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    })

    const chunks = previewQuery.data ? props.text.split(/(\{\{.*?\}\})/) : []

    const allAnswersPlaceholders =
        props.placeholders
            ?.find((placeholder) => placeholder.group === "Custom Questions")
            ?.items.map((item) => item.value) ?? []
    const { answersPlaceholders, commonPlaceholders } = segregatePlaceholders(
        allAnswersPlaceholders,
        previewQuery.data ?? {},
    )

    return (
        <Modal
            centered
            size="30rem"
            title="Sample preview"
            {...props}
            classNames={{
                title: classes.modalTitle,
                content: classes.modalContent,
                body: classes.modalBody,
                header: classes.modalHeader,
            }}
        >
            {previewQuery.isLoading && <LoadingOverlay visible />}
            {previewQuery.isError && <p>Something went wrong. Please try again.</p>}
            <ScrollArea h={250} type="always" scrollbarSize={5} className={classes.paddingContainer}>
                <Stack>
                    <p className={classes.text}>
                        This message will be used together with all custom questions in final message to legislators
                    </p>
                    <div className={classes.preview}>
                        {chunks.map((chunk, index) => {
                            if (commonPlaceholders[chunk]) {
                                return (
                                    <span key={index} className={classes.placeholder}>
                                        {commonPlaceholders[chunk]}
                                    </span>
                                )
                            }
                            if (answersPlaceholders[chunk]) {
                                return (
                                    <span key={index} className={classes.answer}>
                                        {answersPlaceholders[chunk]}
                                    </span>
                                )
                            }
                            return chunk
                        })}
                    </div>
                    <p className={classes.text}>
                        Colored markdown text are responses to <strong>Default fields</strong> and{" "}
                        <strong>Custom questions</strong>
                    </p>
                </Stack>
                <div className={classes.footer}>
                    <Button variant="secondary" onClick={props.onClose}>
                        Close
                    </Button>
                </div>
            </ScrollArea>
        </Modal>
    )
}
